import React, { useContext } from 'react';
import { useFormikContext } from 'formik';
import { ArnContext } from 'contexts';
import { ArnApplicationFormData } from 'types/forms/arnApplicationTypes';
import { Button, Loader } from 'components/common';
import {
  AuSelect,
  AuTextInput,
  ExperianAddressLookup
} from 'components/DTA';
import { IAddressComponents } from 'services/arnService/arnServiceTypes';
import { STATES } from 'arnConstants';

interface IAddressName {
  label: string;
  addressType: string;
}

const AddressDetails: React.FC<IAddressName> = ({ label, addressType }) => {
  const { referenceDataResponse } = useContext(ArnContext);
  const { values, setFieldValue } = useFormikContext<ArnApplicationFormData>();

  const currentFormValues = values[`${addressType}`];
  const selectedCountry = currentFormValues.country;

  const setAddressFields = (addressComponents: IAddressComponents) => {
    if (addressComponents) {
      setFieldValue(`${addressType}.unitNumber`, addressComponents.unit);
      setFieldValue(`${addressType}.streetName`, addressComponents.street);
      setFieldValue(`${addressType}.houseOrBuildingNumber`, addressComponents.streetNumber);
      setFieldValue(`${addressType}.buildingName`, '');
      setFieldValue(`${addressType}.cityOrSuburb`, addressComponents.suburb);
      setFieldValue(`${addressType}.state`, addressComponents.stateCode);
      setFieldValue(`${addressType}.postcode`, addressComponents.postalCode);
    }
  };

  const toggleQuickLookup = () => {
    setFieldValue(`${addressType}.quickLookup`, !currentFormValues.quickLookup);
  };
  //TODO: filtering will not be required once this is implemented in new API endpoint
  const countryList = referenceDataResponse?.value && referenceDataResponse?.value['COUNTRY']
    .map(country => {
      return {
        name: country.casa_name, code: country.casa_code
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name))
    .filter((country, index, self) =>
      index === self.findIndex(value => value.code === country.code)
    );

  if (!countryList || countryList.length <= 0) {
    return <Loader message='Working. Please wait.....' />;
  }

  const showQuickLookup = selectedCountry === 'Australia' && currentFormValues.quickLookup;
  return (<>
    {
      countryList && countryList.length > 0 && <AuSelect
        name={`${addressType}.country`}
        label='Country'
        block
        options={[
          ...countryList.map((country: { name: string; code: string; }) => {
            return {
              text: country.name,
              value: country.name
            };
          })
        ]}
      />
    }
    {
      showQuickLookup &&
      <ExperianAddressLookup
        label={label}
        name={`${addressType}.addressLookup`}
        callback={
          (e: any) => {
            setAddressFields(e);
          }
        }
        block
      />
    }
    {
      !showQuickLookup &&
      <>
        {addressType === 'postalAddress' && <AuTextInput
          name={`${addressType}.poBox`}
          label='P.O. Box (optional)'
          as='input'
          block />}
        <AuTextInput
          name={`${addressType}.unitNumber`}
          label='Unit number or building level (optional)'
          as='input'
          block />

        <AuTextInput
          name={`${addressType}.houseOrBuildingNumber`}
          label='House or building number'
          as='input'
          block />

        <AuTextInput
          name={`${addressType}.buildingName`}
          label='Building name'
          as='input'
          block />

        <AuTextInput
          name={`${addressType}.streetName`}
          label='Street name'
          as='input'
          block />

        <AuTextInput
          name={`${addressType}.cityOrSuburb`}
          label='City, suburb or town'
          as='input'
          block />

        {selectedCountry === 'Australia' && <AuSelect
          name={`${addressType}.state`}
          label='State'
          block
          options={[
            {
              text: 'Please select',
              value: ''
            },
            ...STATES.map((state: { name: string; code: string; }) => {
              return {
                text: state.name,
                value: state.code
              };
            })
          ]} />
        }
        {selectedCountry != 'Australia' && <AuTextInput
          name={`${addressType}.state`}
          label='State'
          as='input'
          block />
        }
        <AuTextInput
          name={`${addressType}.postcode`}
          label='Postcode'
          as='input'
          block />
      </>
    }
    {selectedCountry === 'Australia' &&
      <>
        <br />
        <Button as='secondary' onClick={toggleQuickLookup}>
          {showQuickLookup ? 'Enter address manually' : 'Return to address lookup'}
        </Button>
      </>
    }
  </>);
};

export default AddressDetails;